import {
  Bell,
  ChevronRight,
  History,
  HelpCircle,
  LayoutDashboard,
  Library,
  Folder,
  Package,
  Palette,
  Plus,
  Search,
  Settings,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

type NavId = 'lesson-packs' | 'curriculum' | 'design-repository' | 'assets' | 'settings';

const NAV_ITEMS: { id: NavId; label: string; href: string; icon: typeof LayoutDashboard }[] = [
  { id: 'lesson-packs', label: 'Lesson Packs', href: '/lesson-packs', icon: Package },
  { id: 'curriculum', label: 'Curriculum', href: '/curriculum', icon: Library },
  { id: 'design-repository', label: 'Design Repository', href: '/design-repository', icon: Palette },
  { id: 'assets', label: 'Assets', href: '/assets', icon: Folder },
  { id: 'settings', label: 'Settings', href: '/settings', icon: Settings },
];

interface AppShellProps {
  children: ReactNode;
  /** Override which nav item is highlighted. Defaults to first matching path. */
  activeNav?: NavId;
  /** Slot for the small top-bar leader (e.g. "SCRIPT STUDIO" wordmark). */
  topBarLeader?: ReactNode;
  /** When true, show a search input at the top-left. */
  showSearch?: boolean;
  /** Tabs in the centre of the top bar. */
  tabs?: {
    id: string;
    label: string;
    href?: string;
    active?: boolean;
    onClick?: () => void;
  }[];
  /** Right-side actions (Preview/Deploy buttons, etc.). */
  topBarActions?: ReactNode;
  /** Show the bell + avatar cluster on the right of the top bar. Defaults to true. */
  showProfile?: boolean;
  /** Show a "+ Create New" button at the bottom of the sidebar (used on Script Studio). */
  showCreateButton?: boolean;
}

export function AppShell({
  children,
  activeNav,
  topBarLeader,
  showSearch = true,
  tabs,
  topBarActions,
  showProfile = true,
  showCreateButton = false,
}: AppShellProps) {
  const pathname = useLocation().pathname;
  // Default fallback is 'lesson-packs' — that's the new home route since
  // Dashboard + Scripts were removed.
  const inferredActive: NavId =
    activeNav ??
    NAV_ITEMS.find((n) => pathname === n.href || pathname.startsWith(`${n.href}/`))?.id ??
    'lesson-packs';

  return (
    <div className="flex min-h-screen w-full bg-bg text-fg">
      <Sidebar
        activeNav={inferredActive}
        showCreateButton={showCreateButton}
      />
      <div className="flex min-h-screen flex-1 flex-col">
        <TopBar
          leader={topBarLeader}
          showSearch={showSearch}
          tabs={tabs}
          actions={topBarActions}
          showProfile={showProfile}
        />
        <main className="flex-1 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}

function Sidebar({
  activeNav,
  showCreateButton,
}: {
  activeNav: NavId;
  showCreateButton: boolean;
}) {
  return (
    <aside className="hidden w-[220px] shrink-0 flex-col border-r border-border-subtle bg-bg-panel md:flex">
      <Link
        to="/lesson-packs"
        className="flex items-center gap-2.5 px-5 pt-5 pb-6"
      >
        <span className="grid h-9 w-9 place-items-center rounded-lg bg-accent text-white shadow-pill">
          <BookGlyph />
        </span>
        <span className="leading-tight">
          <span className="block text-[15px] font-semibold tracking-tight text-fg">
            LMS Author
          </span>
          <span className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-fg-subtle">
            Authoring Tool
          </span>
        </span>
      </Link>

      <nav className="mt-1 flex flex-col gap-0.5 px-3">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = item.id === activeNav;
          return (
            <Link
              key={item.id}
              to={item.href}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
                isActive
                  ? 'bg-accent-soft text-accent'
                  : 'text-fg-muted hover:bg-bg-subtle hover:text-fg',
              )}
            >
              <Icon size={16} className={isActive ? 'text-accent' : ''} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="mt-auto flex flex-col gap-3 px-4 pb-5">
        {showCreateButton && (
          <Button variant="primary" size="md" className="w-full">
            <Plus size={14} />
            Create New
          </Button>
        )}
        <button
          type="button"
          className="flex items-center gap-2 self-start rounded-md px-2 py-1.5 text-[12px] text-fg-muted transition-colors hover:bg-bg-subtle hover:text-fg"
        >
          <HelpCircle size={14} />
          Help Center
        </button>
      </div>
    </aside>
  );
}

function TopBar({
  leader,
  showSearch,
  tabs,
  actions,
  showProfile,
}: {
  leader?: ReactNode;
  showSearch: boolean;
  tabs?: AppShellProps['tabs'];
  actions?: ReactNode;
  showProfile: boolean;
}) {
  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-4 border-b border-border-subtle bg-bg-panel/95 px-6 backdrop-blur">
      <div className="flex min-w-[260px] items-center gap-3">
        {leader}
        {showSearch && (
          <div className="relative w-[240px]">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle"
            />
            <input
              type="search"
              placeholder="Search scripts…"
              className="h-9 w-full rounded-md border border-border bg-bg-subtle/60 pl-8 pr-3 text-[13px] text-fg placeholder:text-fg-subtle focus:border-accent focus:bg-white focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
        )}
      </div>

      {tabs && tabs.length > 0 && (
        <nav className="flex items-center gap-6">
          {tabs.map((t) => (
            <TopTab key={t.id} {...t} />
          ))}
        </nav>
      )}

      <div className="ml-auto flex items-center gap-3">
        {actions}
        {showProfile && (
          <>
            <button
              type="button"
              title="Notifications"
              className="relative grid h-9 w-9 place-items-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <Bell size={16} />
              <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-accent" />
            </button>
            <Avatar />
          </>
        )}
      </div>
    </header>
  );
}

function TopTab({
  label,
  href,
  active,
  onClick,
}: {
  id: string;
  label: string;
  href?: string;
  active?: boolean;
  onClick?: () => void;
}) {
  const className = cn(
    'relative inline-flex h-14 items-center text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors',
    active
      ? 'text-accent after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-accent'
      : 'text-fg-subtle hover:text-fg',
  );
  if (href) {
    return (
      <Link to={href} className={className}>
        {label}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {label}
      </button>
    );
  }
  return <span className={className}>{label}</span>;
}

function Avatar() {
  return (
    <div className="grid h-9 w-9 cursor-pointer place-items-center overflow-hidden rounded-full bg-gradient-to-br from-amber-200 via-rose-200 to-fuchsia-300 ring-2 ring-white shadow-pill">
      <span className="text-[12px] font-semibold text-fg">RB</span>
    </div>
  );
}

export function Breadcrumbs({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
      {items.map((item, i) => {
        const last = i === items.length - 1;
        const node = item.href && !last ? (
          <Link to={item.href} className="hover:text-fg">
            {item.label}
          </Link>
        ) : (
          <span className={last ? 'text-fg' : ''}>{item.label}</span>
        );
        return (
          <span key={`${item.label}-${i}`} className="flex items-center gap-1.5">
            {node}
            {!last && <ChevronRight size={12} className="text-fg-subtle/70" />}
          </span>
        );
      })}
    </div>
  );
}

export function HistoryButton({
  label = 'History',
  onClick,
}: {
  label?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="grid h-9 w-9 place-items-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
    >
      <History size={16} />
    </button>
  );
}

function BookGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 5.5A2.5 2.5 0 0 1 7.5 3H18a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H7.5A2.5 2.5 0 0 0 5 21.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 5.5V19a2.5 2.5 0 0 0 2.5 2.5H18"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path d="M9 8h6M9 11h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
